import { LinearProgress, Menu, MenuItem, Stack, SxProps, Typography } from '@mui/material'
import { QuotaRefTenantMaxBulkActionsForOneAction } from 'c1gen/quotas'
import { C1Button, C1LoadingButton, sidebarWidth } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { FloatingBar } from 'components/core/task/bulk-selected/FloatingBar'
import { TaskBulkActionApproveDialog, TaskBulkActionCancelDialog, TaskBulkActionChangePolicyDialog, TaskBulkActionCommentDialog, TaskBulkActionDenyDialog, TaskBulkActionHardResetDialog, TaskBulkActionProvisionCompleteDialog, TaskBulkActionReassignDialog, TaskBulkActionRestartDialog, TaskBulkActionSkipStepDialog } from 'components/core/task/bulk-selected/TaskBulkSelectedActionDialogs'
import { useHasBuiltinRole } from 'components/no-ui/capability'
import { useQuota } from 'components/no-ui/context/tenant-config'
import { moduleStyleType } from 'components/no-ui/styles/styleTypes'
import { taskActionTypeToFormattedMessageMap, taskTypeToString } from 'components/pages/task/type'
import { useAlert } from 'hooks/useAlert'
import { TaskValue } from 'lib/actions/task'
import { RoleID } from 'lib/constants/system'
import { TaskTypeString } from 'lib/constants/task'
import { useSimpleModal } from 'lib/modals'
import { useClientRPC } from 'lib/rpcts'
import { Check, ChevronDown, ChevronUp, LoaderCircle, X } from 'lucide-react'
import { useRouter } from 'next/router'
import { TaskActionsServiceGetSingleBulkActionProgressByIdRequest } from 'pbts/c1/api/task/v1/actions'
import { TaskSearchCountByTypeRequest, TaskSearchCountResponse, TaskSearchRequest } from 'pbts/c1/api/task/v1/search'
import { TaskSearchServiceClient } from 'pbts/c1/api/task/v1/search.client'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { BulkActionStatus, SelectionSliceKeys, useSelections, useSelectionsState } from 'redux_toolkit/slices/selectionsSlice'
import { getBulkActionProgressByIdApi } from 'redux_toolkit/tasks'

const styles: moduleStyleType = {
    progress: {
        height: '8px',
        width: '180px',
    },

    progressFadeOut: {
        opacity: 0,
        pointerEvents: 'none',
        transitionProperty: 'opacity',
        transitionDelay: '3s',
        transitionDuration: '0.5s',
    },
}

const sortedActions = [
    TaskActionType.APPROVE,
    TaskActionType.DENY,
    TaskActionType.PROVISION_COMPLETE,
    TaskActionType.CLOSE,
    TaskActionType.COMMENT,
    TaskActionType.REASSIGN,
    TaskActionType.RESTART,
    TaskActionType.HARD_RESET,
    TaskActionType.CHANGE_POLICY,
    TaskActionType.SKIP_STEP,
]

type TaskBulkActionDropdownProps = {
    variant?: 'default' | 'floating-bar'
    taskSearchRequest?: TaskSearchRequest
    counts?: TaskSearchCountResponse
    isSelectingViaFilter?: boolean
    setIsSelectingViaFilter?: (value: boolean) => void
    hasPendingActions?: boolean
}

const POLL_INTERVAL = 2000

const getFallbackActions = (isSuperAdmin: boolean, taskTypesSet: Set<TaskTypeString>): Set<TaskActionType> => {
    const actions = new Set<TaskActionType>()

    if (isSuperAdmin) {
        // System admins can perform all actions
        actions.add(TaskActionType.REASSIGN)
        actions.add(TaskActionType.CLOSE)
        actions.add(TaskActionType.RESTART)
        actions.add(TaskActionType.HARD_RESET)
        actions.add(TaskActionType.SKIP_STEP)
        actions.add(TaskActionType.CHANGE_POLICY)
    }

    // Regular users get a safe subset of actions
    actions.add(TaskActionType.COMMENT)
    actions.add(TaskActionType.APPROVE)
    actions.add(TaskActionType.DENY)

    // Only show "Mark as provisioned" if task types include grant or revoke
    if (taskTypesSet.has(TaskTypeString.Grant) || taskTypesSet.has(TaskTypeString.Revoke)) {
        actions.add(TaskActionType.PROVISION_COMPLETE)
    }

    return actions
}

const MAX_BY_ID_SELECTIONS_COUNT = 500

export const TaskBulkActionDropdown: FC<TaskBulkActionDropdownProps> = ({
    variant = 'default',
    taskSearchRequest,
    counts,
    isSelectingViaFilter,
    setIsSelectingViaFilter,
    hasPendingActions,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const dispatch = useDispatch()

    const router = useRouter()
    const intl = useIntl()
    const toast = useAlert()
    const shouldKeepBarVisible = useRef(false)

    const maxBulkActions = useQuota(QuotaRefTenantMaxBulkActionsForOneAction)
    const isSuperAdmin = useHasBuiltinRole(RoleID.SuperAdmin)

    const byIdSelectionsMax = Math.min(MAX_BY_ID_SELECTIONS_COUNT, maxBulkActions)

    const {
        actions: { setActiveBulkActionId, setBulkActionStatus, clearSelections },
    } = useSelections<TaskValue>(SelectionSliceKeys.TASKS)

    const {
        bulkActionStatus, activeBulkActionId, selections,
    } = useSelectionsState<TaskValue>(SelectionSliceKeys.TASKS)

    const client = useClientRPC(TaskSearchServiceClient)
    const [isSelectingAllLoading, setIsSelectingAllLoading] = useState(false)
    const [taskTypesSet, setTaskTypesSet] = useState<Set<TaskTypeString>>(new Set())

    // Compute taskTypesSet based on selection mode
    const computedTaskTypesSet = useMemo(() => {
        if (isSelectingViaFilter) {
            // When selecting via filter, use the cached taskTypesSet from successful API response
            return taskTypesSet
        }
        return selections.length > 0
            ? new Set(
                selections.map((task) => TaskTypeString[taskTypeToString(task.type)]),
            )
            : new Set<TaskTypeString>()
    }, [isSelectingViaFilter, taskTypesSet, selections])

    const count = isSelectingViaFilter ? (counts?.count || 0) : selections.length

    const allowedActions = useMemo(() => {
        // Can't calculate allowed actions from all tasks in filter selection mode
        if (isSelectingViaFilter) return getFallbackActions(isSuperAdmin, computedTaskTypesSet)

        return selections.length > 0
            ? selections.reduce(
                (acc, task, index) => (index === 0 ? acc : acc.intersection(new Set(task.actions))),
                new Set<TaskActionType>(selections[0].actions),
            )
            : new Set<TaskActionType>()
    }, [selections, isSelectingViaFilter, isSuperAdmin, computedTaskTypesSet])

    const {
        trigger: triggerGetBulkActionProgressById,
        data,
        isLoading,
    } = getBulkActionProgressByIdApi.useLazyGetSingleBulkActionProgressById()

    const inv = getBulkActionProgressByIdApi.useInvalidateGetSingleBulkActionProgressById()

    useEffect(() => {
        triggerGetBulkActionProgressById(
            TaskActionsServiceGetSingleBulkActionProgressByIdRequest.create({
                bulkActionId: activeBulkActionId,
            }),
            { force: true, notify: true, actionable: !!activeBulkActionId },
        )

        const timeout = setInterval(() => {
            if (activeBulkActionId) inv()
        }, POLL_INTERVAL)

        return () => clearInterval(timeout)
    }, [activeBulkActionId, isLoading, triggerGetBulkActionProgressById, inv])

    useEffect(() => {
        if (data !== undefined && data.totalActions !== 0) {
            const isActionInProgress = !isLoading && data !== undefined &&
                data.totalActions !== data.successActions + data.errorActions

            if (
                !isActionInProgress
                && bulkActionStatus === BulkActionStatus.ACTIVE
                && activeBulkActionId !== ''
            ) {
                shouldKeepBarVisible.current = true
                dispatch(setBulkActionStatus(BulkActionStatus.DONE))
                dispatch(setActiveBulkActionId(''))
            }
        }
    }, [data, isLoading, bulkActionStatus, activeBulkActionId, dispatch]) // eslint-disable-line

    useEffect(() => {
        if (bulkActionStatus === BulkActionStatus.ACTIVE && variant === 'floating-bar') {
            dispatch(setBulkActionStatus(BulkActionStatus.IDLE))
            dispatch(setActiveBulkActionId(''))
            shouldKeepBarVisible.current = false
        }
    }, [router.asPath]) // eslint-disable-line

    const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleDeselectClick = useCallback(() => {
        dispatch(clearSelections())
        setIsSelectingViaFilter?.(false)
        setTaskTypesSet(new Set())
    }, [clearSelections, dispatch, setIsSelectingViaFilter])

    const handleSelectAllInFilter = useCallback(async () => {
        if (taskSearchRequest && client) {
            setIsSelectingAllLoading(true)
            try {
                // We need to get the count by type, so that we can calculate which task types are in the filter
                // and provide accurate allowedActions.
                const response = await client.searchCountByType(TaskSearchCountByTypeRequest.create({ filter: taskSearchRequest }))

                // Compute taskTypesSet from the response
                const types: TaskTypeString[] = []
                if (response.response.countsByType) {
                    if (response.response.countsByType.grantCount > 0) types.push(TaskTypeString.Grant)
                    if (response.response.countsByType.revokeCount > 0) types.push(TaskTypeString.Revoke)
                    if (response.response.countsByType.certifyCount > 0) types.push(TaskTypeString.Certify)
                }
                setTaskTypesSet(new Set(types))

                setIsSelectingViaFilter?.(true)
                dispatch(clearSelections())
            } catch (error) {
                toast(
                    intl.formatMessage({
                        id: 'task_bulk_select_all_error',
                        defaultMessage: 'Failed to stage bulk action: {error}',
                    }, { error: error instanceof Error ? error.message : 'Unknown error' }),
                    'error',
                )
            } finally {
                setIsSelectingAllLoading(false)
            }
        }
    }, [taskSearchRequest, client, clearSelections, dispatch, setIsSelectingViaFilter, toast, intl])

    const handleClose = (): void => {
        setAnchorEl(null)
    }

    const { open: openModal } = useSimpleModal()

    const onMenuItemClick = useCallback(async (dialogToOpen: TaskActionType): Promise<unknown> => {
        handleClose()

        const modalProps = {
            taskSearchRequest,
            taskTypesSet: computedTaskTypesSet,
            isSelectingViaFilter,
            createPendingActions: hasPendingActions,
            accessReviewId: router.query.access_review_id as string, // TODO this feels hacky
        }

        switch (dialogToOpen) {
            case TaskActionType.CLOSE:
                return openModal(TaskBulkActionCancelDialog, modalProps)()
            case TaskActionType.COMMENT:
                return openModal(TaskBulkActionCommentDialog, modalProps)()
            case TaskActionType.RESTART:
                return openModal(TaskBulkActionRestartDialog, modalProps)()
            case TaskActionType.APPROVE:
                return openModal(TaskBulkActionApproveDialog, modalProps)()
            case TaskActionType.DENY:
                return openModal(TaskBulkActionDenyDialog, modalProps)()
            case TaskActionType.REASSIGN:
                return openModal(TaskBulkActionReassignDialog, modalProps)()
            case TaskActionType.HARD_RESET:
                return openModal(TaskBulkActionHardResetDialog, modalProps)()
            case TaskActionType.CHANGE_POLICY:
                return openModal(TaskBulkActionChangePolicyDialog, modalProps)()
            case TaskActionType.PROVISION_COMPLETE:
                return openModal(TaskBulkActionProvisionCompleteDialog, modalProps)()
            case TaskActionType.SKIP_STEP:
                return openModal(TaskBulkActionSkipStepDialog, modalProps)()
            default:
                break
        }

        return Promise.resolve()
    }, [openModal, taskSearchRequest, computedTaskTypesSet, isSelectingViaFilter, hasPendingActions, router.query.access_review_id])

    const sortedAndValidActions = useMemo(() => (allowedActions
        ? [...allowedActions]
            .filter((a) => (variant === 'floating-bar' ? a !== TaskActionType.APPROVE && a !== TaskActionType.DENY : true))
            .filter((a) => (computedTaskTypesSet.has(TaskTypeString.Revoke) && computedTaskTypesSet.has(TaskTypeString.Grant) ?
                a !== TaskActionType.PROVISION_COMPLETE : true))
            .filter((a) => sortedActions.includes(a))
            .sort((a, b) => sortedActions.indexOf(a) - sortedActions.indexOf(b))
        : []), [allowedActions, variant, computedTaskTypesSet])

    const inner = useMemo(() => <Stack direction="row" spacing={4}>
        {bulkActionStatus !== BulkActionStatus.ACTIVE && count !== 0 ? (
            <Stack direction="row" spacing={4} alignItems="center" width="auto">
                <Stack direction="row" spacing={2} alignItems="center" width="auto" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                        {variant === 'floating-bar' &&
                            <>
                                <C1Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<C1Icon icon={Check} />}
                                    onClick={(): Promise<unknown> => onMenuItemClick(TaskActionType.APPROVE)}
                                    disabled={count > maxBulkActions}
                                >
                                    <FormattedMessage
                                        id="tasks_selected_certify_selected"
                                        defaultMessage="Certify selected"
                                    />
                                </C1Button>
                                <C1Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<C1Icon icon={X} />}
                                    onClick={(): Promise<unknown> => onMenuItemClick(TaskActionType.DENY)}
                                    disabled={count > maxBulkActions}
                                >
                                    <FormattedMessage
                                        id="tasks_selected_deny_selected"
                                        defaultMessage="Deny selected"
                                    />
                                </C1Button>
                            </>
                        }
                        <C1Button
                            aria-controls={open ? 'actions-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}
                            endIcon={open ? <C1Icon icon={ChevronUp} /> : <C1Icon icon={ChevronDown} />}
                            disabled={count > maxBulkActions || selections.length > byIdSelectionsMax}
                            variant={variant === 'floating-bar' ? 'outlined' : 'contained'}
                        >
                            <FormattedMessage
                                id="tasks_selected_tasks count"
                                defaultMessage="{count} selected"
                                values={{ count }}
                            />
                        </C1Button>
                    </Stack>
                    {(selections.length > byIdSelectionsMax && !isSelectingViaFilter) && (
                        <Typography>
                            <FormattedMessage
                                id="component_bulk_action_max_limit"
                                defaultMessage="You can manually select up to {max} tasks"
                                values={{ max: byIdSelectionsMax }}
                            />
                        </Typography>
                    )}

                    <Stack direction="row" spacing={1} width="auto">
                        {!isSelectingViaFilter && taskSearchRequest && counts?.count
                            && counts.count > count && counts.count <= maxBulkActions && (
                            <C1LoadingButton
                                onClick={handleSelectAllInFilter}
                                variant="text"
                                loading={isSelectingAllLoading}
                            >
                                <FormattedMessage
                                    id="tasks_selected_select_all_in_filter"
                                    defaultMessage="Select all {total_count} in filter"
                                    values={{ total_count: counts.count }}
                                />
                            </C1LoadingButton>
                        )}
                        <C1Button onClick={handleDeselectClick} variant="text">
                            <FormattedMessage
                                id="tasks_selected_deselect"
                                defaultMessage="Clear selection"
                            />
                        </C1Button>
                    </Stack>
                </Stack>

                <Menu
                    id="actions-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    slotProps={{ paper: { sx: { marginTop: -1, width: '160px' } } }}
                    MenuListProps={{ 'aria-labelledby': 'actions-button' }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transitionDuration={100}
                >
                    {sortedAndValidActions.map((action) => (
                        <MenuItem key={action} onClick={(): Promise<unknown> => onMenuItemClick(action)}>
                            <BulkFormattedMessage action={action} set={computedTaskTypesSet} />
                        </MenuItem>
                    ))}
                </Menu>
            </Stack>
        ) : (
            <C1Button
                startIcon={<C1Icon icon={LoaderCircle} />}
                disabled
                variant="contained"
                sx={bulkActionStatus === BulkActionStatus.ACTIVE ? { opacity: 1 } : styles.progressFadeOut}
            >
                <FormattedMessage
                    id="bulk_action_processing"
                    defaultMessage="Processing"
                />
            </C1Button>
        )}

        {bulkActionStatus !== BulkActionStatus.IDLE && (
            <Stack
                direction="row"
                alignItems="center"
                spacing={4}
                sx={bulkActionStatus === BulkActionStatus.ACTIVE ? { opacity: 1 } : styles.progressFadeOut}
            >
                {bulkActionStatus === BulkActionStatus.DONE ? (
                    <>
                        <LinearProgress variant="determinate" value={100} sx={styles.progress} />
                        <Typography>
                            <FormattedMessage
                                id="component_bulk_action_complete"
                                defaultMessage="Bulk action completed"
                            />
                        </Typography>
                    </>
                ) : data && !isLoading ? ((): React.ReactNode => {
                    const { errorActions, successActions, totalActions } = data
                    const value = ((errorActions + successActions) / totalActions) * 100
                    return <LinearProgress variant="determinate" value={Number.isNaN(value) ? 0 : value} sx={styles.progress} />
                })() : (<LinearProgress key="blank" variant="determinate" value={0} sx={styles.progress} />)}
            </Stack>
        )}
    </Stack>, [
        bulkActionStatus, count, variant, maxBulkActions, open, handleClick,
        selections.length, byIdSelectionsMax, isSelectingViaFilter,
        taskSearchRequest, counts?.count, handleSelectAllInFilter,
        isSelectingAllLoading, handleDeselectClick, anchorEl,
        sortedAndValidActions, data, isLoading, onMenuItemClick,
        computedTaskTypesSet,
    ])

    return variant !== 'floating-bar'
        ? inner
        : (count !== 0 || bulkActionStatus === BulkActionStatus.ACTIVE || shouldKeepBarVisible.current) && <FloatingBar
            onTransitionEnd={(): void => { shouldKeepBarVisible.current = false }}
            sx={{
                left: sidebarWidth,
                mx: 5,
                ...(bulkActionStatus === BulkActionStatus.ACTIVE || count !== 0
                    ? { opacity: 1 }
                    : styles.progressFadeOut as SxProps),
            }}
        >
            {inner}
        </FloatingBar>
}

interface BulkFormattedMessageProps {
    action: TaskActionType
    set: Set<TaskTypeString>
}

const BulkFormattedMessage: FC<BulkFormattedMessageProps> = ({ action, set }) => {
    if (action === TaskActionType.APPROVE && set.size === 1) {
        if (set.has(TaskTypeString.Certify)) { return <FormattedMessage id="task_action_type_certify" defaultMessage="Certify" /> }
        if (set.has(TaskTypeString.Revoke)) { return <FormattedMessage id="task_action_type_confirm" defaultMessage="Confirm" /> }
    }

    if (action === TaskActionType.DENY && set.size === 1) {
        if (set.has(TaskTypeString.Certify)) { return <FormattedMessage id="task_action_type_revoke" defaultMessage="Deny" /> }
        if (set.has(TaskTypeString.Revoke)) { return <FormattedMessage id="task_action_type_decline" defaultMessage="Decline" /> }
    }

    if (action === TaskActionType.PROVISION_COMPLETE && set.size === 1) {
        if (set.has(TaskTypeString.Revoke)) { return <FormattedMessage id="task_action_type_deprovision" defaultMessage="Mark deprovisioned" /> }
    }

    return taskActionTypeToFormattedMessageMap.get(action)
}
